'use client';

import { c as _c } from "react/compiler-runtime";
import { trackEvent } from '@svs/nova-core/analytics';
import { useDialog } from '@svs/nova-foundation/nextjs/dialog';
import { resetCoupon } from '@svs/nova-poolbetting/core/action/coupon';
import { useCouponId, useCouponStatus } from '@svs/nova-poolbetting/core/nextjs/coupon';
import { COUPON_SETTINGS_SHEET, QUICK_PICK_SHEET } from '@svs/nova-poolbetting/schema/input';
import { CouponToolbox, CouponToolboxButton, CouponToolboxContent } from '@svs/nova-poolbetting/ui/CouponToolbox';
import { TipsenLink } from '@svs/nova-poolbetting/ui/TipsenLink';
import Dustbin from '@svs/ui/react/icons/Dustbin';
import Pix from '@svs/ui/react/icons/Pix';
import Settings from '@svs/ui/react/icons/Settings';
import Spelguide from '@svs/ui/react/icons/Spelguide';
import { useMediaQueryAbove } from '@svs/ui/react/media-query';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@svs/ui/react/tooltip';
import { createElement, type ReactNode, Suspense, useCallback } from 'react';
import { AddCouponsContainer, OpenSheetLink } from '@/components/index.browser';
import { usePathname } from '@/lib/index.browser';
const handleTracking = (label: string, pathname: string): void => {
  trackEvent({
    category: 'Nya tipsen',
    action: label,
    optLabel: pathname
  });
};

/**
 * Coupon toolbox for the play page.
 *
 * @returns Coupon toolbox for the play page.
 */
export default function PlayCouponToolbox() {
  const $ = _c(66);
  const [couponId, couponIndex] = useCouponId();
  const {
    showDialog
  } = useDialog();
  const isLargeScreen = useMediaQueryAbove("md");
  const pathname = usePathname();
  let t0;
  if ($[0] !== couponId || $[1] !== couponIndex) {
    t0 = {
      couponId,
      couponIndex
    };
    $[0] = couponId;
    $[1] = couponIndex;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const couponStatus = useCouponStatus(t0);
  let t1;
  if ($[3] !== couponId || $[4] !== couponIndex) {
    t1 = () => {
      (async () => {
        await resetCoupon({
          couponId,
          couponIndex
        });
      })();
    };
    $[3] = couponId;
    $[4] = couponIndex;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const resetCouponAction = t1;
  let t2;
  if ($[6] !== resetCouponAction || $[7] !== showDialog) {
    t2 = event => {
      event.preventDefault();
      (async () => {
        await showDialog({
          title: "Dina tecken kommer att rensas",
          description: createElement("p", {
            className: "text-center"
          }, "Vill du forts\xE4tta \xE4nd\xE5?"),
          buttons: {
            primary: {
              label: "Rensa",
              action: resetCouponAction
            },
            secondary: {
              label: "Avbryt"
            }
          }
        });
      })();
    };
    $[6] = resetCouponAction;
    $[7] = showDialog;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const handleResetCoupon = t2;
  const t3 = isLargeScreen ? "md" : "sm";
  let t4;
  if ($[9] !== pathname) {
    t4 = {
      category: "Nya tipsen",
      action: "Fyll i med PIX!",
      optLabel: pathname
    };
    $[9] = pathname;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Pix />;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4) {
    t6 = <OpenSheetLink sheet={QUICK_PICK_SHEET} queriesToKeep="ALL" trackingObject={t4}>{t5}Fyll i med PIX!</OpenSheetLink>;
    $[12] = t4;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t3 || $[15] !== t6) {
    t7 = <Suspense><CouponToolboxButton size={t3} asChild={true}>{t6}</CouponToolboxButton></Suspense>;
    $[14] = t3;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== pathname) {
    t8 = () => {
      handleTracking("Spelguide", pathname);
    };
    $[17] = pathname;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const t9 = isLargeScreen ? "md" : "sm";
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <TipsenLink scroll={false} aria-label="Spelguide" queriesToKeep="ALL" href={{
      query: {
        modal: "gameGuide"
      }
    }}><Spelguide /></TipsenLink>;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t8 || $[21] !== t9) {
    t11 = <TooltipTrigger asChild={true}><CouponToolboxButton onClick={t8} size={t9} asChild={true} containerClassName="lg:hidden" className="min-w-0 px-2 md:px-3 lg:min-w-18">{t10}</CouponToolboxButton></TooltipTrigger>;
    $[20] = t8;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <TooltipContent side="top">Spelguide</TooltipContent>;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t11) {
    t13 = <Tooltip>{t11}{t12}</Tooltip>;
    $[24] = t11;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== pathname) {
    t14 = () => {
      handleTracking("L\xE4gg till kuponger", pathname);
    };
    $[26] = pathname;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const t15 = isLargeScreen ? "md" : "sm";
  let t16;
  if ($[28] !== t14 || $[29] !== t15) {
    t16 = <TooltipTrigger asChild={true}><AddCouponsContainer onClick={t14} size={t15} /></TooltipTrigger>;
    $[28] = t14;
    $[29] = t15;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <TooltipContent side="top">Lägg till kupong</TooltipContent>;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== t16) {
    t18 = <Tooltip>{t16}{t17}</Tooltip>;
    $[32] = t16;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  const t19 = isLargeScreen ? "md" : "sm";
  let t20;
  if ($[34] !== pathname) {
    t20 = {
      category: "Nya tipsen",
      action: "Anpassa",
      optLabel: pathname
    };
    $[34] = pathname;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Settings />;
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  let t22;
  if ($[37] !== t20) {
    t22 = <OpenSheetLink sheet={COUPON_SETTINGS_SHEET} aria-label={"Kuponginst\xE4llningar"} queriesToKeep="ALL" trackingObject={t20}>{t21}</OpenSheetLink>;
    $[37] = t20;
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  let t23;
  if ($[39] !== t19 || $[40] !== t22) {
    t23 = <TooltipTrigger asChild={true}><CouponToolboxButton size={t19} asChild={true} className="min-w-0 px-2 md:px-3 lg:min-w-18">{t22}</CouponToolboxButton></TooltipTrigger>;
    $[39] = t19;
    $[40] = t22;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  let t24;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <TooltipContent side="top">Inställningar</TooltipContent>;
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  let t25;
  if ($[43] !== t23) {
    t25 = <Suspense><Tooltip>{t23}{t24}</Tooltip></Suspense>;
    $[43] = t23;
    $[44] = t25;
  } else {
    t25 = $[44];
  }
  const t26 = couponStatus === "INVALID_NOT_STARTED";
  let t27;
  if ($[45] !== pathname) {
    t27 = () => {
      handleTracking("Rensa", pathname);
    };
    $[45] = pathname;
    $[46] = t27;
  } else {
    t27 = $[46];
  }
  const t28 = isLargeScreen ? "md" : "sm";
  let t29;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <Dustbin />;
    $[47] = t29;
  } else {
    t29 = $[47];
  }
  let t30;
  if ($[48] !== t26 || $[49] !== t27 || $[50] !== t28) {
    t30 = <TooltipTrigger asChild={true}><CouponToolboxButton className="am:ml-auto w-full min-w-0 px-2 md:px-3 lg:min-w-18" aria-label="Rensa kupong" disabled={t26} data-testid="clearCoupon" onClick={t27} type="submit" size={t28}>{t29}</CouponToolboxButton></TooltipTrigger>;
    $[48] = t26;
    $[49] = t27;
    $[50] = t28;
    $[51] = t30;
  } else {
    t30 = $[51];
  }
  let t31;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <TooltipContent side="top">Rensa kupong</TooltipContent>;
    $[52] = t31;
  } else {
    t31 = $[52];
  }
  let t32;
  if ($[53] !== t30) {
    t32 = <Tooltip>{t30}{t31}</Tooltip>;
    $[53] = t30;
    $[54] = t32;
  } else {
    t32 = $[54];
  }
  let t33;
  if ($[55] !== handleResetCoupon || $[56] !== t32) {
    t33 = <form onSubmit={handleResetCoupon}>{t32}</form>;
    $[55] = handleResetCoupon;
    $[56] = t32;
    $[57] = t33;
  } else {
    t33 = $[57];
  }
  let t34;
  if ($[58] !== t13 || $[59] !== t18 || $[60] !== t25 || $[61] !== t33) {
    t34 = <TooltipProvider><div className="flex gap-2">{t13}{t18}{t25}{t33}</div></TooltipProvider>;
    $[58] = t13;
    $[59] = t18;
    $[60] = t25;
    $[61] = t33;
    $[62] = t34;
  } else {
    t34 = $[62];
  }
  let t35;
  if ($[63] !== t34 || $[64] !== t7) {
    t35 = <CouponToolbox><CouponToolboxContent className="bg-surface justify-between pt-0">{t7}{t34}</CouponToolboxContent></CouponToolbox>;
    $[63] = t34;
    $[64] = t7;
    $[65] = t35;
  } else {
    t35 = $[65];
  }
  return t35;
}